/*
 * FlowCarousel —— 3D 覆盖流（coverflow）轮播的几何 / 时间参数。
 *
 * 全部数值来自参考动画 carousel-flow-alpha.mp4（实为 VP9+Alpha 的 WebM，
 * 1200x870、30fps、18.207s）的逐帧反解：
 *   - 逐帧提取带 alpha 的 PNG，按列扫描 alpha 轮廓，得到每张卡片投影后的梯形；
 *   - 梯形上/下边直线的交点即该卡片的灭点，7 个灭点的 y 全部落在 363，
 *     故 perspective-origin 的 y = 363（x = 1200/2 = 600）；
 *   - 由灭点 x 解出 rotateY，再由外边缘的位置/高度解出 translateX / translateZ；
 *     透视距离 P 与深度存在整体缩放自由度，这里取 P = 1200px；
 *   - 各槽位不透明度由“仅该卡片可见”的色带反解得到：1 / 0.724 / 0.447 / 0.157，
 *     近似 1 - |d| / 3.56，因此 |d| = 3.5 处收敛到 0，正好用作环形回绕点；
 *   - 一轮位移 78 帧 = 2600ms，其中运动 16 帧 ≈ 533ms，
 *     缓动曲线拟合为 cubic-bezier(0.58, 0, 0.45, 1)。
 * 复刻校验：按下表渲染的合成帧与原视频 alpha 通道 MAE ≈ 4.8/255。
 */

/** 设计稿舞台尺寸，所有数值均以此为坐标系 */
export const STAGE = { W: 1200, H: 870 };

/** 透视距离与透视原点 */
export const PERSPECTIVE = 1200;
export const ORIGIN = { X: 600, Y: 363 };

/** 卡片盒子：外框（金边）的尺寸与在舞台内的静止位置 */
export const CARD = {
  W: 493,
  H: 745,
  X: 353.5,
  Y: 44.85,
  RADIUS: 20
};

/** 超出该距离的卡片完全透明，也是环形回绕点（见文件头说明） */
export const SPAN = 3.5;

/**
 * 槽位表：d 为距中心的槽位距离（非负），负向槽位取镜像。
 * rotateY 为左侧卡片的角度，右侧取反；translateX 同理。
 */
export const SLOTS = [
  { d: 0, rotateY: 0, translateX: 0, translateZ: 0, opacity: 1 },
  {
    d: 1,
    rotateY: 60.813,
    translateX: -268.36,
    translateZ: -235.83,
    opacity: 0.724
  },
  {
    d: 2,
    rotateY: 70.693,
    translateX: -512.0,
    translateZ: -457.31,
    opacity: 0.447
  },
  {
    d: 3,
    rotateY: 75.973,
    translateX: -752.81,
    translateZ: -686.5,
    opacity: 0.157
  },
  { d: SPAN, rotateY: 77.9, translateX: -873, translateZ: -800, opacity: 0 }
];

/**
 * 中心卡在「停留期」里会缓慢等比放大（参考动画里的呼吸感）。
 * 逐帧量到的是一条直线：h = 0.838·帧 + 720.5，62 帧从 720.5px 长到 771.6px
 * （拟合残差 rms 0.26px），宽高比例一致，所以是等比 scale。
 * 换算到 CARD.H = 745 的基准即下面两个值，全程放大约 7.1%。
 * 侧边卡片全程不动（整帧轮廓最左 5 / 最右 1194 逐帧不变），故只作用于中心。
 */
export const CENTER_ZOOM = { from: 0.9671, to: 1.0357 };

/** 默认节奏：一轮 2600ms，其中位移 533ms */
export const TIMING = { interval: 2600, duration: 533 };

/** 指示点：7 个固定位置，激活项就地展开为胶囊 */
export const DOTS = {
  CX: 600,
  CY: 824.5,
  PITCH: 20,
  SIZE: 8,
  ACTIVE_W: 24,
  COLOR: [207, 207, 207],
  ACTIVE_COLOR: [200, 164, 108]
};

/**
 * 取任意实数槽位（含小数）的变换，用于位移过程中的插值。
 * @param {number} d 带符号的槽位距离，负数在左、正数在右
 * @returns {{rotateY:number, translateX:number, translateZ:number, opacity:number}}
 */
export function slotAt(d) {
  const abs = Math.abs(d);
  const sign = d < 0 ? 1 : -1; // 左侧卡片 rotateY / translateX 取正向
  if (abs >= SPAN) {
    const edge = SLOTS[SLOTS.length - 1];
    return {
      rotateY: sign * edge.rotateY,
      translateX: sign * edge.translateX,
      translateZ: edge.translateZ,
      opacity: 0
    };
  }
  let i = 1;
  while (i < SLOTS.length - 1 && SLOTS[i].d < abs) i++;
  const a = SLOTS[i - 1];
  const b = SLOTS[i];
  const t = b.d === a.d ? 0 : (abs - a.d) / (b.d - a.d);
  return {
    rotateY: sign * (a.rotateY + (b.rotateY - a.rotateY) * t),
    translateX: sign * (a.translateX + (b.translateX - a.translateX) * t),
    translateZ: a.translateZ + (b.translateZ - a.translateZ) * t,
    opacity: a.opacity + (b.opacity - a.opacity) * t
  };
}

/**
 * 某张卡片当前的缩放。
 *
 * 权重 `1 - |d|` 让缩放只发生在中心附近，交接时两边都是连续的：
 * 让出中心的那张按停留进度停在终点值上、随权重衰减回 1；
 * 还没到过中心的那张以起点值入场，落位时停留进度正好归零，接得上。
 *
 * @param {number} d 带符号的槽位距离
 * @param {number} holdT 停留进度 0→1
 */
export function zoomAt(d, holdT) {
  const w = 1 - Math.abs(d);
  if (w <= 0) return 1;
  const z =
    d <= 0
      ? CENTER_ZOOM.from + (CENTER_ZOOM.to - CENTER_ZOOM.from) * holdT
      : CENTER_ZOOM.from;
  return 1 + (z - 1) * w;
}

/**
 * 生成 cubic-bezier(x1, y1, x2, y2) 缓动函数（与 CSS 同解）。
 * @returns {(t:number)=>number}
 */
export function cubicBezier(x1, y1, x2, y2) {
  const cx = 3 * x1;
  const bxc = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bxc;
  const cy = 3 * y1;
  const byc = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - byc;
  const sampleX = t => ((ax * t + bxc) * t + cx) * t;
  const sampleY = t => ((ay * t + byc) * t + cy) * t;
  const slopeX = t => (3 * ax * t + 2 * bxc) * t + cx;
  return function ease(x) {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const dx = sampleX(t) - x;
      if (Math.abs(dx) < 1e-6) return sampleY(t);
      const d = slopeX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= dx / d;
    }
    let lo = 0;
    let hi = 1;
    t = x;
    while (lo < hi) {
      const v = sampleX(t);
      if (Math.abs(v - x) < 1e-6) break;
      if (v < x) lo = t;
      else hi = t;
      const next = (lo + hi) / 2;
      if (Math.abs(next - t) < 1e-7) break;
      t = next;
    }
    return sampleY(t);
  };
}

/** 参考动画使用的缓动 */
export const EASE = cubicBezier(0.58, 0, 0.45, 1);
